import {Component, Input} from "@angular/core";
import {UntypedFormBuilder, Validators} from "@angular/forms";
import {FormComponent} from "../form.component";
import {I18NextService} from "angular-i18next";
import {PortNumberBlockDetails} from "../../phonenumber/port-phonenumber.model";
import {compareNumber} from "@myshared/compareNumberValidator";
import {isValidPhoneNumber} from "libphonenumber-js";

type Form = {
  countryPrefix: string;
  areaCode: string;
  number: string;
  start: string;
  end: string;
  previewStart: string;
  previewEnd: string;
}

@Component({
    selector: 'app-port-block-number-form',
    templateUrl: './port-block-number-form.component.html',
    styleUrls: [],
    standalone: false
})
export class PortBlockNumberFormComponent extends FormComponent<PortNumberBlockDetails> {
  @Input() submitButtonLabel = 'app_update';
  @Input() areaCode: string;
  @Input() countryPrefix: string = '0049';
  @Input() countryCode: string;

  constructor(protected fb: UntypedFormBuilder, protected i18next: I18NextService) {
    super(fb, i18next);
  }

  public formMapper(form: Form): PortNumberBlockDetails {
    const number = new PortNumberBlockDetails();
    number.country_prefix = '00' + +form.countryPrefix;
    number.area_code = +form.areaCode + '';
    number.main_number = +form.number + '';
    number.block_start = number.formatLeadingZeros(form.start, form.end);
    number.block_end = form.end;

    return number;
  }

  public get areaCodePlaceholder() {
    const ph = this.countryCode ? `_${this.countryCode.toLowerCase()}_` : '_';
    return `port_number${ph}areacode_placeholder`;
  }

  public validate(): boolean {
    if (!this.isAssembledNumberValid()) {
      const numberCtrl = this.form.get('number')!;
      numberCtrl.markAsTouched();
      numberCtrl.markAsDirty();
      numberCtrl.setErrors({ ...(numberCtrl.errors ?? {}), invalidPhoneNumber: true });
      return false;
    }
    return true;
  }

  private isAssembledNumberValid(): boolean {
    const { areaCode, number } = this.form.getRawValue();
    const countryPrefix = this.form.getRawValue().countryPrefix;
    if (!number || !areaCode) return false;
    const fullNumber = `${countryPrefix}${+areaCode}${number}`;
    try {
      return isValidPhoneNumber(fullNumber, this.countryCode as any ?? 'DE');
    } catch {
      return false;
    }
  }

  protected onFormChanged(r: any) {
    const values = this.formMapper(this.getValues());
    const { start, end } = values.previewBlock();

    this.form.get('previewStart').patchValue(start, {emitEvent: false});
    this.form.get('previewEnd').patchValue(end, {emitEvent: false});
  }

  protected createForm() {
    const prefixNumber = '+' + +this.countryPrefix;

    this.form = this.fb.group({
      countryPrefix: [{value: prefixNumber, disabled: true}],
      areaCode: [{value: this.areaCode ?? '', disabled: !!this.areaCode}, [Validators.required, Validators.maxLength(6), Validators.pattern('^[0-9]+')]],
      number: ['', [Validators.required, Validators.pattern('^[0-9]+')]],
      start: ['', [Validators.required, Validators.pattern('^[0-9]+'), Validators.maxLength(4)]],
      end: ['', [
        Validators.required,
        Validators.pattern('^[0-9]+'),
        Validators.maxLength(4),
        compareNumber(['start', '>='])
      ]],
      previewStart: [{value: prefixNumber, disabled: true}],
      previewEnd: [{value: prefixNumber, disabled: true}],
    })
  }

  protected changeForm() {
    if (!this.form) return;
    const prefixNumber = '+' + +this.countryPrefix;

    this.form.get('countryPrefix').setValue(prefixNumber);
    this.form.get('areaCode').setValue(this.areaCode);
    if (!!this.areaCode) {
      this.form.get('areaCode').disable();
    } else {
      this.form.get('areaCode').enable();
    }
    this.form.get('previewStart').setValue(prefixNumber);
    this.form.get('previewEnd').setValue(prefixNumber);
  }
}
